import {
  useRecurringJobsFormData,
  useSubmitRecurringJob,
} from "@/modules/routes/recurring-jobs-routes";
import { addressValidation, setAddressInitialValues } from "@/utils";
import { useEffect, useMemo } from "react";
import * as yup from "yup";
import polyglot from "@/utils/polyglot";
import { useDocumentTitle } from "@/modules/hooks";
import Layout from "../Layout";

// Import our split components
import AddressSelection from "../AddressSelection";
import DateSelection from "../DateSelection";
import FrequencySelection from "../FrequencySelection";
import JobberSelection from "../JobberSelection";
import PricePerHourSelection from "../PricePerHourSelection";
import ServiceTypeSelection from "../ServiceTypeSelection";
import TimeHoursSelection from "../TimeHourSelection";

const RecurringJobsNew = () => {
  const submitRecurringJob = useSubmitRecurringJob();
  const privateJobberId = new URLSearchParams(window.location.search).get(
    "jobber_id"
  );

  const documentTitle = useDocumentTitle();
  useEffect(() => {
    documentTitle.set(polyglot.t("recurring.create_recurring"));
  }, [documentTitle]);

  const { data, isFetching, isLoading } = useRecurringJobsFormData(
    {
      jobber_id: privateJobberId,
    },
    { enabled: !!privateJobberId }
  );

  const handleSubmit = (values) => {
    submitRecurringJob.mutate(values);
  };

  const pages = useMemo(
    () => [
      ...(!privateJobberId
        ? {
            path: "jobber",
            title: polyglot.t("recurring.select_jobber"),
            View: JobberSelection,
            validationSchema: yup.object().shape({
              private_jobber_id: yup.string().required(),
            }),
          }
        : []),
      {
        path: "date",
        title: polyglot.t("recurring.select_date"),
        View: DateSelection,
        validationSchema: yup.object().shape({
          start_date: yup.string().required(),
        }),
      },
      {
        path: "frequency",
        title: polyglot.t("recurring.frequency"),
        View: FrequencySelection,
        validationSchema: yup.object().shape({
          frequency: yup.string().required(),
        }),
      },
      {
        path: "service",
        title: polyglot.t("recurring.select_service"),
        View: ServiceTypeSelection,
        validationSchema: yup.object().shape({
          category: yup.number().required(),
        }),
      },
      {
        path: "time-hour",
        title: polyglot.t("recurring.select_time_hour"),
        View: TimeHoursSelection,
        validationSchema: yup.object().shape({
          start_hour: yup.number().required(),
          nb_hours: yup
            .number()
            .min(1, polyglot.t("categories.minimal_duration_error"))
            .required(),
        }),
      },
      {
        path: "price-per-hour",
        title: polyglot.t("recurring.select_price_per_hour"),
        View: PricePerHourSelection,
        customProps: {
          data,
        },
        validationSchema: yup.object().shape({
          price_per_hour: yup.number().required(),
        }),
      },
      {
        path: "address",
        title: polyglot.t("recurring.select_address"),
        View: AddressSelection,
        validationSchema: yup.object().shape(
          {
            ...addressValidation,
          },
          [
            ["place_id", "address_id"],
            ["address_name", "city_id"],
          ]
        ),
      },
    ],
    [privateJobberId, data]
  );

  return (
    <Layout
      data={data}
      pages={privateJobberId && isLoading ? [] : pages}
      onSubmit={handleSubmit}
      isLoading={
        isFetching ||
        submitRecurringJob.isLoading ||
        submitRecurringJob.isSuccess
      }
      initialValues={{
        private_jobber_id: data?.jobber?.id || undefined,
        ...setAddressInitialValues({
          address: data?.addresses[0]?.address || "",
          address_id: data?.addresses[0]?.id || "",
        }),
      }}
    />
  );
};

export default RecurringJobsNew;
